import { TenantConfigAccess, TenantConfigPlane } from '@today/types'
import { TenantConfigConsoleView, TenantConfigSectionPlaceholder } from '@today/ui-domain'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { TenantConfigLoadFailure, useTenantConfigManifest } from '@/hooks/useTenantConfigManifest'
import {
	resolveSectionComponent,
	TENANT_CONFIG_SECTION_REGISTRY,
	type TenantConfigSectionRegistry,
} from '@/modules/tenant-config/section-registry'
import { ForbiddenPage } from '@/pages/errors/Forbidden'
import { NotFoundPage } from '@/pages/NotFound'
import type { TenantConfigManifest } from '@/services/tenant-config.service'

export interface TenantConfigPageProps {
	plane: TenantConfigPlane
	/** Injectable for tests; defaults to the shipped registry. */
	registry?: TenantConfigSectionRegistry
}

const PLANE_LABEL: Record<TenantConfigPlane, string> = {
	[TenantConfigPlane.PLATFORM]: 'Platform administration',
	[TenantConfigPlane.TENANT]: 'Tenant administration',
}

/**
 * The tenant configuration console (TO-374), mounted on both planes:
 * `/admin/tenants/:tenantId/config/:section?` (Super-Admin) and
 * `/t/:slug/admin/config/:section?` (Tenant Admin). It renders only what the
 * server's manifest returns; a refused manifest renders no console at all.
 */
export function TenantConfigPage({ plane, registry = TENANT_CONFIG_SECTION_REGISTRY }: TenantConfigPageProps) {
	const { tenantId = '', slug = '', section } = useParams()
	const target = plane === TenantConfigPlane.PLATFORM ? tenantId : slug
	const base = plane === TenantConfigPlane.PLATFORM ? `/admin/tenants/${tenantId}/config` : `/t/${slug}/admin/config`
	const { manifest, failure, retry } = useTenantConfigManifest(plane, target)

	if (failure === TenantConfigLoadFailure.FORBIDDEN) return <ForbiddenPage />
	if (failure === TenantConfigLoadFailure.NOT_FOUND) return <NotFoundPage />
	if (failure) {
		return (
			<div role="alert" className="flex flex-col items-start gap-3 p-6 text-sm text-ds-danger">
				<p>Could not load the tenant configuration.</p>
				<button type="button" onClick={retry} className="rounded-md border px-3 py-1">
					Retry
				</button>
			</div>
		)
	}
	if (!manifest) {
		return (
			<p className="p-6 text-sm text-ds-text-muted" aria-live="polite">
				Loading configuration…
			</p>
		)
	}
	return <ConsoleFor manifest={manifest} base={base} plane={plane} registry={registry} section={section} />
}

interface ConsoleForProps {
	manifest: TenantConfigManifest
	base: string
	plane: TenantConfigPlane
	registry: TenantConfigSectionRegistry
	section: string | undefined
}

/** The loaded console: redirects a missing / non-manifest section to the first one. */
function ConsoleFor({ manifest, base, plane, registry, section }: ConsoleForProps) {
	const navigate = useNavigate()
	const active = manifest.sections.find((s) => s.key === section)
	const first = manifest.sections[0]
	if (!active) return first ? <Navigate to={`${base}/${first.key}`} replace /> : null

	const readOnly = active.access === TenantConfigAccess.READ
	const title = registry[active.key].title
	const Section = resolveSectionComponent(registry, active.key)
	return (
		<TenantConfigConsoleView
			tenantName={manifest.tenant.name}
			planeLabel={PLANE_LABEL[plane]}
			sections={manifest.sections.map((s) => ({
				key: s.key,
				title: registry[s.key].title,
				readOnly: s.access === TenantConfigAccess.READ,
			}))}
			activeKey={active.key}
			onSelect={(key) => void navigate(`${base}/${key}`)}
		>
			{Section ? (
				<Section
					tenantId={manifest.tenant.id}
					tenantSlug={manifest.tenant.slug}
					plane={plane}
					section={active.key}
					readOnly={readOnly}
				/>
			) : (
				<TenantConfigSectionPlaceholder title={title} />
			)}
		</TenantConfigConsoleView>
	)
}
